import * as Popover from '@radix-ui/react-popover'
import { clsx } from 'clsx'
import { sv } from 'date-fns/locale'
import { startTransition, useId, useState } from 'react'
import { DayPicker, type SelectSingleEventHandler } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { Text } from 'ui/src/components/Text/Text'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { LockIcon } from 'ui/src/icons/LockIcon'
import { tokens } from 'ui/src/theme/tokens.css'
import { useHighlightAnimation } from 'ui/src/utils/useHighlightAnimation'
import { visuallyHidden } from 'ui/src/utils/visuallyHidden.css'
import { LoadingDots } from '@/components/LoadingDots/LoadingDots'
import { formatAPIDate } from '@/utils/date'
import { Language } from '@/utils/l10n/types'
import { useCurrentLocale } from '@/utils/l10n/useCurrentLocale'
import { useFormatter } from '@/utils/useFormatter'
import { trigger, iconWrapper, chevronIcon, popoverContent, dayPicker } from './InputDay.css'

import 'react-day-picker/dist/style.css'

export type InputDayProps = {
  label: string
  size?: 'small' | 'medium' | 'large'
  id?: string
  name?: string
  selected?: Date
  defaultSelected?: Date
  onSelect?: (day: Date) => void
  fromDate?: Date
  toDate?: Date
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  loading?: boolean
  className?: string
}

export function InputDay(props: InputDayProps) {
  const { t } = useTranslation()
  const formatter = useFormatter()
  const { language } = useCurrentLocale()

  const [value, setValue] = useState(props.selected ?? props.defaultSelected)
  const [open, setOpen] = useState(props.autoFocus ?? false)

  const autoIdentifier = useId()
  const inputId = props.id ?? autoIdentifier

  const shouldBeDisabled = props.disabled || props.loading

  const { highlight, animationProps } = useHighlightAnimation<HTMLButtonElement>()

  const handleSelect: SelectSingleEventHandler = (day) => {
    if (day) {
      setValue(day)
      props.onSelect?.(day)
    }
    highlight()
    setOpen(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (open && shouldBeDisabled) return
    startTransition(() => {
      setOpen(open)
    })
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        {...animationProps}
        className={clsx(trigger[props.size ?? 'medium'], props.className)}
        disabled={shouldBeDisabled}
        data-loading={props.loading}
      >
        <div>
          <label htmlFor={inputId}>
            <Text
              as="span"
              size="xs"
              color={shouldBeDisabled ? 'textTertiary' : 'textTranslucentSecondary'}
              singleLine={true}
            >
              {props.label}
            </Text>
          </label>
          <Text size="md" color={!value || shouldBeDisabled ? 'textTertiary' : 'textPrimary'}>
            {value ? formatter.fromNow(value) : t('DATE_INPUT_EMPTY_LABEL')}
          </Text>
        </div>

        <div className={iconWrapper}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {props.loading ? (
            <LoadingDots color={tokens.legacyColors.textPrimary} />
          ) : props.disabled ? (
            <LockIcon size="1rem" color={tokens.legacyColors.textSecondary} />
          ) : (
            <ChevronIcon className={chevronIcon.animated} size="1rem" />
          )}
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={popoverContent} side="bottom" align="start" sideOffset={-4} alignOffset={-4}>
          <DayPicker
            className={dayPicker}
            mode="single"
            captionLayout="dropdown"
            locale={language === Language.Sv ? sv : undefined}
            required={props.required}
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value}
            startMonth={props.fromDate}
            endMonth={props.toDate}
            disabled={[
              ...(props.fromDate ? [{ before: props.fromDate }] : []),
              ...(props.toDate ? [{ after: props.toDate }] : []),
            ]}
            weekStartsOn={1}
            components={{
              Chevron(props) {
                const orientation = props.orientation ?? 'down'
                return (
                  <ChevronIcon
                    className={chevronIcon[orientation]}
                    size={['up', 'down'].includes(orientation) ? '0.65rem' : '1rem'}
                  />
                )
              },
            }}
          />
        </Popover.Content>
      </Popover.Portal>

      <input
        id={inputId}
        className={visuallyHidden}
        // This input is only used for form submission and should not be focusable
        tabIndex={-1}
        aria-hidden={true}
        name={props.name}
        value={value ? formatAPIDate(value) : undefined}
        disabled={props.disabled}
        // Even though we pass 'required' to 'useInput' 'inputProps' props bag doesn't include
        // it so we need to add it manually here.
        required={props.required}
      />
    </Popover.Root>
  )
}
